import api from "@/lib/api";

const MAX_VIDEO_DURATION_S = 60;
const MAX_VIDEO_BYTES = 50 * 1024 * 1024; // 50 MB

export async function uploadToCloudinary(file) {
  const { data: sig } = await api.get("/cloudinary/signature", { params: { resource_type: "image" } });
  const form = new FormData();
  form.append("file", file);
  form.append("api_key", sig.api_key);
  form.append("timestamp", sig.timestamp);
  form.append("signature", sig.signature);
  form.append("folder", sig.folder);

  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
    { method: "POST", body: form }
  );
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Upload failed: ${err}`);
  }
  return res.json(); // { secure_url, public_id, ... }
}

/** Read duration metadata from a local file without uploading. */
function readVideoDuration(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(video.duration);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read video metadata"));
    };
    video.src = url;
  });
}

/**
 * Upload a short video clip to Cloudinary.
 * Hard limits: ≤60s, ≤50 MB. Returns a VideoRef-shaped object with the
 * Cloudinary URL, an auto-extracted poster frame URL, duration, and bytes.
 */
export async function uploadVideoToCloudinary(file, { onProgress } = {}) {
  if (!file.type.startsWith("video/")) {
    throw new Error("File must be a video");
  }
  if (file.size > MAX_VIDEO_BYTES) {
    throw new Error(`Video too large (max ${(MAX_VIDEO_BYTES / 1024 / 1024).toFixed(0)} MB)`);
  }
  // Validate duration client-side BEFORE upload to save bandwidth.
  let duration = 0;
  try {
    duration = await readVideoDuration(file);
  } catch {
    // If we can't read it, let the upload proceed — Cloudinary will return its own duration.
  }
  if (duration > MAX_VIDEO_DURATION_S + 0.5) {
    throw new Error(`Clip too long — ${Math.round(duration)}s. Max is ${MAX_VIDEO_DURATION_S}s.`);
  }

  const { data: sig } = await api.get("/cloudinary/signature", { params: { resource_type: "video" } });

  // Use XHR so we can stream upload progress
  const result = await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `https://api.cloudinary.com/v1_1/${sig.cloud_name}/video/upload`);
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(JSON.parse(xhr.responseText));
      } else {
        reject(new Error(`Upload failed: HTTP ${xhr.status} ${xhr.responseText}`));
      }
    };
    xhr.onerror = () => reject(new Error("Upload network error"));
    const form = new FormData();
    form.append("file", file);
    form.append("api_key", sig.api_key);
    form.append("timestamp", sig.timestamp);
    form.append("signature", sig.signature);
    form.append("folder", sig.folder);
    xhr.send(form);
  });

  // Build a poster URL by swapping /video/upload → /video/upload/.../so_auto and
  // serving as .jpg. We use Cloudinary's `so_auto` (smart frame) for a clean
  // thumbnail. Format: https://res.cloudinary.com/{cloud}/video/upload/so_auto,q_auto/{public_id}.jpg
  const posterUrl = `https://res.cloudinary.com/${sig.cloud_name}/video/upload/so_auto,q_auto,f_jpg/${result.public_id}.jpg`;

  return {
    url: result.secure_url,
    public_id: result.public_id,
    poster_url: posterUrl,
    duration: Number(result.duration || duration || 0),
    bytes: Number(result.bytes || file.size || 0),
    width: result.width || null,
    height: result.height || null,
  };
}

export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export const VIDEO_LIMITS = { maxDurationSeconds: MAX_VIDEO_DURATION_S, maxBytes: MAX_VIDEO_BYTES };
